import { AccessPoint } from "./access-point";
import { AddressInfo } from "./address-info";
import { Author } from "./author";
import { BankAccount } from "./bank-account";
import { Chat } from "./chat";
import { ChatMedia } from "./chat-media";
import { Collection } from "./collection";
import { Configuration } from "./configuration";
import { Currency } from "./currency";
import { DeliveryFeeEstimate } from "./delivery-fee-estimate";
import { Destination } from "./destination";
import { Event } from "./event";
import { EventBundle } from "./event-bundle";
import { EventLocation } from "./event-location";
import { EventTicket, EventTicketNo } from "./event-ticket";
import { Host } from "./host";
import { Member } from "./member";
import { Organisation } from "./organisation";
import { OrganisationSettings } from "./organisation-settings";
import { PaymentItem } from "./payment-item";
import { Poll, PollData } from "./poll";
import { PollCandidate } from "./poll-candidate";
import { PollFinish } from "./poll-finish";
import { PollQuestion } from "./poll-question";
import { Product } from "./product";
import { Ticket } from "./ticket";
import { TrackLink } from "./track-link";
import { asNumber, isObject, parseList } from "./model-utils";
export * from "./enums";

const applyPaginationMetadata = (parsed, data) => {
  const total = asNumber(data.total ?? data.tolal);
  const totalPages = asNumber(data.totalPages ?? data.totalPage);
  const limit = asNumber(data.limit);
  const currentPage = asNumber(data.currentPage ?? data.page);
  const randomSeed = data.randomSeed;
  const searchText = data.searchText;

  if (total !== undefined) parsed.total = total;
  if (totalPages !== undefined) parsed.totalPages = totalPages;
  if (limit !== undefined) parsed.limit = limit;
  if (currentPage !== undefined) parsed.currentPage = currentPage;
  if (randomSeed !== undefined) parsed.randomSeed = randomSeed;
  if (searchText !== undefined) parsed.searchText = searchText;

  return parsed;
};

export const parseProduct = (value) => Product.fromJson(value);

export const parseCollection = (value) => Collection.fromJson(value);

export const parseAccessPoint = (value) => AccessPoint.fromJson(value);

export const parseOrganisation = (value) => Organisation.fromJson(value);

export const parseMember = (value) => Member.fromJson(value);

export const parseDestination = (value) => Destination.fromJson(value);

export const parseEvent = (value) => Event.fromJson(value);

export const parseTicket = (value) => Ticket.fromJson(value);

export const parseEventTicket = (value) => EventTicket.fromJson(value);

export const parseEventTicketNo = (value) => EventTicketNo.fromJson(value);

export const parsePoll = (value) => Poll.fromJson(value);

export const parsePollDataModel = (value) => PollData.fromJson(value);

export const parsePollQuestion = (value) => PollQuestion.fromJson(value);

export const parsePollCandidate = (value) => PollCandidate.fromJson(value);

export const parsePollFinish = (value) => PollFinish.fromJson(value);

export const parseConfiguration = (value) => Configuration.fromJson(value);

export const parseCurrency = (value) => Currency.fromJson(value);

export const parseChat = (value) => Chat.fromJson(value);

export const parseAddressInfo = (value) => AddressInfo.fromJson(value);

export const parseDeliveryFeeEstimate = (value) =>
  DeliveryFeeEstimate.fromJson(value);

export const parseAddressInfoResponse = (response) => {
  if (!isObject(response)) return response;

  const parsedResponse = { ...response };
  if (isObject(response.data)) {
    parsedResponse.data = parseAddressInfo(response.data);
  }

  return parsedResponse;
};

export const parseDeliveryFeeEstimateResponse = (response) => {
  if (!isObject(response)) return response;

  const parsedResponse = { ...response };
  if (isObject(response.data)) {
    parsedResponse.data = parseDeliveryFeeEstimate(response.data);
  }

  return parsedResponse;
};

export const parseApiResponse = (response) => {
  if (!isObject(response)) return response;

  const parsedResponse = { ...response };
  if (!isObject(response.data)) return parsedResponse;

  const data = response.data;
  if (!isObject(data)) return data;

  const parsed = { ...data };

  if ("product" in data) parsed.product = parseProduct(data.product);
  if ("products" in data) {
    parsed.products = parseList(data.products, parseProduct);
  }

  if ("collection" in data) parsed.collection = parseCollection(data.collection);
  if ("collections" in data) {
    parsed.collections = parseList(data.collections, parseCollection);
  }

  if ("accessPoint" in data) {
    parsed.accessPoint = parseAccessPoint(data.accessPoint);
  }
  if ("accessPoints" in data) {
    parsed.accessPoints = parseList(data.accessPoints, parseAccessPoint);
  }

  if ("organisation" in data) {
    parsed.organisation = parseOrganisation(data.organisation);
  }
  if ("organisations" in data) {
    parsed.organisations = parseList(data.organisations, parseOrganisation);
  }

  if ("member" in data) parsed.member = parseMember(data.member);
  if ("members" in data) parsed.members = parseList(data.members, parseMember);

  if ("destination" in data) {
    parsed.destination = parseDestination(data.destination);
  }
  if ("destinations" in data) {
    parsed.destinations = parseList(data.destinations, parseDestination);
  }

  if ("event" in data) parsed.event = parseEvent(data.event);
  if ("events" in data) parsed.events = parseList(data.events, parseEvent);

  if ("ticket" in data) parsed.ticket = parseTicket(data.ticket);
  if ("ticketItem" in data) {
    parsed.ticketItem = EventBundle.fromJson(data.ticketItem);
  }
  if ("tickets" in data) parsed.tickets = parseList(data.tickets, parseTicket);

  if ("poll" in data) parsed.poll = parsePoll(data.poll);
  if ("polls" in data) parsed.polls = parseList(data.polls, parsePoll);
  if ("pollData" in data) parsed.pollData = parsePollDataModel(data.pollData);
  if ("pollFinish" in data) {
    parsed.pollFinish = parsePollFinish(data.pollFinish);
  }
  if ("pollQuestion" in data) {
    parsed.pollQuestion = parsePollQuestion(data.pollQuestion);
  }
  if ("pollQuestions" in data) {
    parsed.pollQuestions = parseList(data.pollQuestions, parsePollQuestion);
  }
  if ("pollCandidate" in data) {
    parsed.pollCandidate = parsePollCandidate(data.pollCandidate);
  }
  if ("pollCandidates" in data) {
    parsed.pollCandidates = parseList(data.pollCandidates, parsePollCandidate);
  }

  if ("config" in data) parsed.config = parseConfiguration(data.config);
  if ("currencies" in data) {
    parsed.currencies = parseList(data.currencies, parseCurrency);
  }

  if ("comment" in data && isObject(data.comment)) {
    parsed.comment = parseChat(data.comment);
  }

  if ("comments" in data) {
    parsed.comments = parseList(data.comments, parseChat);
  }

  parsedResponse.data = applyPaginationMetadata(parsed, data);
  return parsedResponse;
};

export {
  AccessPoint,
  AddressInfo,
  Author,
  BankAccount,
  Chat,
  ChatMedia,
  Collection,
  Configuration,
  Currency,
  DeliveryFeeEstimate,
  Destination,
  Event,
  EventBundle,
  EventLocation,
  EventTicket,
  EventTicketNo,
  Host,
  Member,
  Organisation,
  OrganisationSettings,
  PaymentItem,
  Poll,
  PollCandidate,
  PollData,
  PollFinish,
  PollQuestion,
  Product,
  Ticket,
  TrackLink,
};
